import api from '@/core/api/client';
import { isAxiosError } from 'axios';

export type SubscriptionStatus =
  | 'TRIALING'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'CANCELED'
  | 'PENDING_PAYMENT';

export interface PlanBasic {
  id: string;
  name: string;
  slug: string;
  price: number;
  currency: string;
  interval: string;
  limits?: Record<string, number> | null;
}

export interface CompanyBasic {
  id: string;
  name: string;
  slug: string;
}

export interface Subscription {
  id: string;
  companyId: string;
  planId: string;
  status: SubscriptionStatus;
  gatewayProvider?: string | null;
  gatewaySubscriptionId?: string | null;
  metadata?: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd?: string | null;
  canceledAt?: string | null;
  canceledBy?: string | null;
  canceledReason?: string | null;
  createdAt: string;
  updatedAt: string;
  plan?: PlanBasic | null;
  company?: CompanyBasic | null;
}

export interface SubscriptionsListParams {
  search?: string;
  page?: number;
  limit?: number;
  companyId?: string;
  planId?: string;
  status?: SubscriptionStatus;
}

export interface SubscriptionOverview {
  totalSubscriptions: number;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  overdueSubscriptions: number;
  pendingSubscriptions: number;
  canceledSubscriptions: number;
  monthly: Array<{ month: string; count: number }>;
  plans: Array<{ planId: string; name: string; count: number }>;
}

export interface ProductBasic {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  featureKey: string;
  amount: number;
  isModule: boolean;
  billingType?: string;
}

export interface SubscriptionItemWithProduct {
  id: string;
  subscriptionId: string;
  productId: string;
  quantity: number;
  priceAtMoment: number;
  status?: string;
  createdAt: string;
  updatedAt: string;
  product: ProductBasic;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const subscriptionsService = {
  async overview(): Promise<SubscriptionOverview> {
    const response = await api.get('/subscriptions/overview');
    if (response.data.success) return response.data.data;
    throw new Error('Não foi possível carregar os indicadores de assinaturas.');
  },
  async list(params: SubscriptionsListParams = {}): Promise<PaginatedResponse<Subscription>> {
    const response = await api.get('/subscriptions', { params });
    if (response.data.success) {
      return { data: response.data.data, pagination: response.data.pagination };
    }
    throw new Error(response.data.message || 'Erro ao listar assinaturas');
  },

  async get(id: string): Promise<Subscription> {
    const response = await api.get(`/subscriptions/${id}`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao buscar assinatura');
  },

  async getByCompany(companyId: string): Promise<Subscription | null> {
    const response = await api.get(`/subscriptions/company/${companyId}`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao buscar assinatura da empresa');
  },

  async create(data: { companyId: string; planId: string; trialDays?: number }): Promise<Subscription> {
    const response = await api.post('/subscriptions', data);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao criar assinatura');
  },

  async cancel(id: string, reason?: string): Promise<Subscription> {
    const response = await api.post(`/subscriptions/${id}/cancel`, { reason });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao cancelar assinatura');
  },

  async renew(id: string): Promise<Subscription> {
    const response = await api.post(`/subscriptions/${id}/renew`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao renovar assinatura');
  },

  /**
   * Ativa assinatura manualmente (staff bypass gateway). Usado quando o
   * piloto não tem gateway de pagamento configurado e subscription fica
   * presa em PENDING_PAYMENT. Idempotente: se já está ACTIVE, retorna.
   */
  async activateManual(id: string, notes?: string): Promise<Subscription> {
    const response = await api.post(`/subscriptions/${id}/activate-manual`, { notes });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao ativar manualmente a assinatura');
  },

  async listItems(subscriptionId: string): Promise<SubscriptionItemWithProduct[]> {
    const response = await api.get(`/subscriptions/${subscriptionId}/items`);
    if (response.data.success) return response.data.data ?? [];
    throw new Error(response.data.message || 'Erro ao listar itens da assinatura');
  },

  async addItem(
    subscriptionId: string,
    productId: string,
    quantity: number,
    opts?: { unitPriceCents?: number; prorate?: boolean }
  ): Promise<SubscriptionItemWithProduct> {
    const body: {
      productId: string;
      quantity: number;
      unitPriceCents?: number;
      prorate?: boolean;
    } = { productId, quantity };
    if (opts?.unitPriceCents !== undefined) {
      body.unitPriceCents = opts.unitPriceCents;
    }
    if (opts?.prorate === true) {
      body.prorate = true;
    }
    const response = await api.post(`/subscriptions/${subscriptionId}/items`, body);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao adicionar item');
  },

  async removeItem(itemId: string, opts?: { prorate?: boolean }): Promise<void> {
    const params = opts?.prorate ? { prorate: 'true' } : undefined;
    const response = await api.delete(`/subscriptions/items/${itemId}`, { params });
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao remover item');
  },

  /** Ajusta preço unitário (desconto/cortesia) e/ou quantidade; sincroniza total com o gateway quando houver vínculo. */
  async updateItem(
    itemId: string,
    body: { unitPriceCents?: number; quantity?: number; prorate?: boolean }
  ): Promise<SubscriptionItemWithProduct> {
    try {
      const response = await api.patch(`/subscriptions/items/${itemId}`, body);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Erro ao atualizar item');
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.data && typeof (e.response.data as { message?: string }).message === 'string') {
        throw new Error((e.response.data as { message: string }).message);
      }
      throw e instanceof Error ? e : new Error('Erro ao atualizar item');
    }
  },
};
